/**
 * COMPOSABLE: useOrganization
 *
 * Maneja toda la lógica relacionada con el estado de la organización multi-tenant:
 * - Validaciones de estado (activa, trial, expirado)
 * - Verificación de permisos de acceso
 * - Advertencias y mensajes al usuario
 * - Enforcement de restricciones
 *
 * MULTI-TENANT: Este composable es crítico para la seguridad y UX
 * del sistema multi-tenant, asegurando que organizaciones inactivas
 * o con trial expirado no puedan operar.
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

/**
 * Composable principal para manejo de organización
 *
 * @returns {Object} Propiedades y métodos para gestionar organización
 */
export function useOrganization() {
  const authStore = useAuthStore()
  const router = useRouter()

  // =============================================
  // COMPUTED PROPERTIES
  // =============================================

  /**
   * Organización actual del usuario autenticado
   */
  const organization = computed(() => authStore.organization)

  /**
   * Si la organización está activa
   * Default: true si no hay organización (no bloquear login)
   */
  const isActive = computed(() => {
    if (!organization.value) return true
    return organization.value.is_active ?? true
  })

  /**
   * Si la organización está en período de prueba
   */
  const isTrial = computed(() => {
    if (!organization.value) return false
    return organization.value.is_trial ?? false
  })

  /**
   * Días restantes de trial
   */
  const trialDaysRemaining = computed(() => authStore.trialDaysRemaining)

  /**
   * Si el trial ha expirado
   */
  const isTrialExpired = computed(() => authStore.isTrialExpired)

  /**
   * Nombre de la organización
   */
  const organizationName = computed(() => authStore.organizationName || 'Mi Negocio')

  /**
   * Slug de la organización
   */
  const organizationSlug = computed(() => authStore.organizationSlug || '')

  /**
   * Si la organización puede operar normalmente
   *
   * REGLAS DE NEGOCIO:
   * - Organización debe estar activa
   * - Trial no debe estar expirado
   */
  const canOperate = computed(() => {
    // Si no está activa, no puede operar
    if (!isActive.value) {
      console.warn('⚠️ Organización inactiva, no puede operar')
      return false
    }

    // Si trial expiró, no puede operar
    if (isTrialExpired.value) {
      console.warn('⚠️ Trial expirado, no puede operar')
      return false
    }

    return true
  })

  /**
   * Estado legible de la organización
   */
  const organizationStatus = computed(() => {
    if (!isActive.value) {
      return {
        status: 'inactive',
        message: 'Organización desactivada',
        canWork: false,
        severity: 'error'
      }
    }

    if (isTrialExpired.value) {
      return {
        status: 'trial_expired',
        message: 'Período de prueba expirado',
        canWork: false,
        severity: 'error'
      }
    }

    if (isTrial.value && trialDaysRemaining.value !== null && trialDaysRemaining.value <= 3) {
      return {
        status: 'trial_ending',
        message: `Trial: ${trialDaysRemaining.value} días restantes`,
        canWork: true,
        severity: 'warning'
      }
    }

    if (isTrial.value) {
      return {
        status: 'trial_active',
        message: 'Período de prueba activo',
        canWork: true,
        severity: 'info'
      }
    }

    return {
      status: 'active',
      message: 'Organización activa',
      canWork: true,
      severity: 'success'
    }
  })

  // =============================================
  // MÉTODOS DE VALIDACIÓN
  // =============================================

  /**
   * Verificar si puede acceder a una ruta específica
   *
   * @param {string} routeName - Nombre de la ruta
   * @returns {boolean}
   */
  const canAccessRoute = (routeName) => {
    // Si no hay organización, permitir solo login
    if (!organization.value) {
      return routeName === 'Login'
    }

    // Si organización inactiva, solo permitir login
    if (!isActive.value) {
      console.warn('🚫 Organización inactiva, solo login permitido')
      return routeName === 'Login'
    }

    // Si trial expiró, bloquear POS pero permitir otras rutas
    if (isTrialExpired.value) {
      const blockedRoutes = ['POS']
      const isBlocked = blockedRoutes.includes(routeName)

      if (isBlocked) {
        console.warn(`🚫 Trial expirado, ruta ${routeName} bloqueada`)
      }

      return !isBlocked
    }

    // En todos los demás casos, permitir acceso
    return true
  }

  /**
   * Verificar si debe mostrar advertencias
   *
   * @returns {Object|null} - Objeto con info de advertencia o null
   */
  const getWarningIfNeeded = () => {
    // Organización inactiva
    if (!isActive.value) {
      return {
        type: 'error',
        title: 'Organización desactivada',
        message: 'Tu organización ha sido desactivada. Contacta a soporte para más información.',
        action: 'logout'
      }
    }

    // Trial expirado
    if (isTrialExpired.value) {
      return {
        type: 'error',
        title: 'Período de prueba expirado',
        message: 'El período de prueba ha expirado. Por favor contacta a soporte para activar tu plan y continuar usando HanamiPOS.',
        action: 'block_pos'
      }
    }

    // Trial próximo a expirar (≤ 3 días)
    if (isTrial.value && trialDaysRemaining.value !== null && trialDaysRemaining.value <= 3) {
      return {
        type: 'warning',
        title: `Trial: ${trialDaysRemaining.value} día${trialDaysRemaining.value !== 1 ? 's' : ''} restante${trialDaysRemaining.value !== 1 ? 's' : ''}`,
        message: 'Tu período de prueba está por terminar. Contacta a soporte para activar tu plan y evitar interrupciones.',
        action: 'none'
      }
    }

    return null
  }

  /**
   * Forzar logout si organización está inactiva
   *
   * @returns {boolean} - true si forzó logout
   */
  const enforceInactiveOrganization = () => {
    if (!isActive.value && organization.value) {
      console.error('🚫 Forzando logout por organización inactiva')

      authStore.logout()

      router.push({
        name: 'Login',
        query: {
          error: 'organization_inactive',
          message: 'Tu organización ha sido desactivada. Contacta a soporte.'
        }
      })

      return true
    }

    return false
  }

  /**
   * Bloquear acceso al POS si trial expiró
   *
   * @returns {boolean} - true si bloqueó
   */
  const enforceTrialExpiration = () => {
    if (isTrialExpired.value) {
      console.warn('⚠️ Bloqueando acceso a POS por trial expirado')

      router.push({
        name: 'Login',
        query: {
          error: 'trial_expired',
          message: 'El período de prueba ha expirado. Contacta a soporte.'
        }
      })

      return true
    }

    return false
  }

  /**
   * Validar que puede trabajar en el POS
   *
   * @returns {Object} - { canWork: boolean, reason: string|null }
   */
  const validateCanWork = () => {
    if (!isActive.value) {
      return {
        canWork: false,
        reason: 'organization_inactive',
        message: 'Tu organización ha sido desactivada'
      }
    }

    if (isTrialExpired.value) {
      return {
        canWork: false,
        reason: 'trial_expired',
        message: 'El período de prueba ha expirado'
      }
    }

    return {
      canWork: true,
      reason: null,
      message: null
    }
  }

  // =============================================
  // RETORNO PÚBLICO
  // =============================================

  return {
    // Estado
    organization,
    isActive,
    isTrial,
    trialDaysRemaining,
    isTrialExpired,
    organizationName,
    organizationSlug,

    // Computed
    canOperate,
    organizationStatus,

    // Métodos de validación
    canAccessRoute,
    getWarningIfNeeded,
    validateCanWork,

    // Métodos de enforcement
    enforceInactiveOrganization,
    enforceTrialExpiration
  }
}

/**
 * DOCUMENTACIÓN DE USO:
 *
 * 1. IMPORTAR EN COMPONENTE:
 * ```javascript
 * import { useOrganization } from '@/composables/useOrganization'
 *
 * export default {
 *   setup() {
 *     const org = useOrganization()
 *     return { org }
 *   }
 * }
 * ```
 *
 * 2. USAR EN TEMPLATE:
 * ```html
 * <div v-if="!org.canOperate">
 *   <p>{{ org.organizationStatus.message }}</p>
 * </div>
 * ```
 *
 * 3. VALIDAR ANTES DE OPERACIONES:
 * ```javascript
 * const handleSale = () => {
 *   const validation = org.validateCanWork()
 *   if (!validation.canWork) {
 *     alert(validation.message)
 *     return
 *   }
 *   // Proceder con la venta
 * }
 * ```
 *
 * 4. EN ROUTER GUARDS:
 * ```javascript
 * router.beforeEach((to, from, next) => {
 *   const org = useOrganization()
 *   if (!org.canAccessRoute(to.name)) {
 *     next({ name: 'Login' })
 *     return
 *   }
 *   next()
 * })
 * ```
 *
 * REGLAS DE NEGOCIO:
 * - Organización inactiva: Logout forzado
 * - Trial expirado: Bloquear POS, permitir otras rutas
 * - Trial ≤ 3 días: Mostrar advertencia pero permitir trabajo
 * - Trial > 3 días: Operación normal
 */
